import { Permissions } from '@restore/permission-catalog';

type ScopeChecker = (scopeKey: string) => boolean;

/**
 * Can this user reach the Labor Management screens?
 *
 * Not every role that should is granted the umbrella "Labor Management" permission — a
 * DOR/therapist gets the granular Create/Update/Delete ones instead, plus the THIN-7846
 * own/team permissions. Gating on the umbrella alone hid the whole section (and 404'd its
 * routes) for those users.
 *
 * THIN-8096: "Edit Own Labor" was missing from this chain, so a role granted only that
 * permission — exactly the therapist shape THIN-7846 introduced it for — got no Labor
 * Management nav section and no route, and the only way to surface the page was to grant
 * the legacy Create/Update Labor Management pair that own-labor is meant to replace.
 * "Edit Team Labor" was missing for the same reason; it implies View Team Labor, but a
 * role whose saved grants never picked up that auto-enable would have fallen through too.
 *
 * Shared by the sidebar and the router so the two can't drift: a nav entry that renders
 * but 404s on click is worse than one that is simply absent.
 */
export const hasLaborManagementAccess = (has: ScopeChecker): boolean =>
  has(Permissions.LABOR_MANAGEMENT) ||
  has(Permissions.CREATE_LABOR_MANAGEMENT) ||
  has(Permissions.UPDATE_LABOR_MANAGEMENT) ||
  has(Permissions.DELETE_LABOR_MANAGEMENT) ||
  has(Permissions.VIEW_TEAM_LABOR_MANAGEMENT) ||
  has(Permissions.EDIT_OWN_LABOR_MANAGEMENT) ||
  has(Permissions.EDIT_TEAM_LABOR_MANAGEMENT);

/**
 * May this user act on labor entries (edit / delete)?
 *
 * Drives the table's Actions column: a DOR/RDO with edit rights sees it, a read-only
 * viewer does not. Edit Own Labor and Edit Team Labor are the only permissions the write
 * endpoints accept — see {@link canWriteLaborEntry}.
 *
 * Column-level only — whether a given row's Edit/Delete items are offered is a per-entry
 * question, since Edit Own Labor is scoped to entries the user owns. See
 * {@link canEditLaborEntry} / {@link canDeleteLaborEntry}.
 */
export const hasLaborEditAccess = (has: ScopeChecker): boolean =>
  has(Permissions.EDIT_OWN_LABOR_MANAGEMENT) ||
  has(Permissions.EDIT_TEAM_LABOR_MANAGEMENT);

/**
 * THIN-8096: may this user write THIS labor entry?
 *
 * Mirrors `LaborEditAccessPolicy.canEditEntry` exactly — `hasEditTeamAccess() ||
 * (hasEditOwnAccess() && isOwner(target))` — so the control the user sees matches what the API
 * will accept instead of offering an action that 403s.
 *
 * The legacy Create/Update/Delete Labor Management permissions are deliberately absent. They are
 * still ACTIVE rows and they still open the Labor Management screens via
 * {@link hasLaborManagementAccess}, but the server-side write policy does not consult them at
 * all, so accepting them here renders controls whose saves are refused. No prod role holds the
 * legacy trio without also holding Edit Own or Edit Team, so nothing loses a capability it can
 * actually exercise.
 */
const canWriteLaborEntry = (has: ScopeChecker, isOwnEntry: boolean): boolean =>
  has(Permissions.EDIT_TEAM_LABOR_MANAGEMENT) ||
  (isOwnEntry && has(Permissions.EDIT_OWN_LABOR_MANAGEMENT));

/**
 * May this user edit this labor entry?
 *
 * Identical to {@link canDeleteLaborEntry} today because the backend guards update and delete
 * with the same `assertCanEditEntry` call. Kept as two names so the call sites say which action
 * they are gating, and so a future split needs no change at the call site.
 */
export const canEditLaborEntry = (has: ScopeChecker, isOwnEntry: boolean): boolean =>
  canWriteLaborEntry(has, isOwnEntry);

/** May this user delete this labor entry? */
export const canDeleteLaborEntry = (has: ScopeChecker, isOwnEntry: boolean): boolean =>
  canWriteLaborEntry(has, isOwnEntry);

/**
 * THIN-8096: may this user add a labor entry at all?
 *
 * The Add control checked only the legacy Create Labor Management permission, so a role holding
 * just Edit Own Labor reached the page with a permanently disabled button. Creating an entry
 * runs through the same `assertCanEditEntry` gate as editing one — against the therapist named
 * in the request body — so the own/team permissions are what grant it, and legacy Create alone
 * would enable a button whose save is refused.
 */
export const hasLaborCreateAccess = (has: ScopeChecker): boolean =>
  has(Permissions.EDIT_OWN_LABOR_MANAGEMENT) ||
  has(Permissions.EDIT_TEAM_LABOR_MANAGEMENT);


export const hasLaborTeamViewAccess = (has: ScopeChecker): boolean =>
  has(Permissions.VIEW_OTHER_THERAPIST_AVAILABILITY) ||
  // Raw scopeKeys until the catalog is regenerated — see hasLaborManagementAccess.
  has('VIEW_TEAM_LABOR_MANAGEMENT') ||
  has('EDIT_TEAM_LABOR_MANAGEMENT');

/**
 * May this user act on the approval workflow — approve / unapprove / not-approve an entry,
 * and approve the pay period? Without it the Labor Report & Approval table is read-only.
 *
 * "Approve Labor Management" is not in the installed catalog; it resolves through the
 * bridge map in use-permission.tsx until the catalog is regenerated.
 */
export const hasLaborApproveAccess = (has: ScopeChecker): boolean =>
  has('APPROVE_LABOR_MANAGEMENT');

/**
 * May this user act on the Labor Report & Approval table — approve / unapprove /
 * not-approve an entry and approve the pay period?
 *
 * This is ONE permission row with two names. Its scopeKey is APPROVE_LABOR_MANAGEMENT (see
 * hasLaborApproveAccess), which is what the backend emits in currentScopes and checks server-side
 * for hasWriteAccess. Its display text in every tenant is "Edit Labor Validation", which is what
 * the human-name fallback in use-permission.tsx matches when currentScopes is absent (the physician
 * profile path, or a backend that does not dual-emit). Accept either so the gate holds on both
 * resolution paths; checking only the raw 'EDIT_LABOR_VALIDATION' key is false whenever scopes are
 * present, since no row carries that scopeKey. See THIN-7995 and THIN-7981.
 */
export const hasLaborValidationEditAccess = (has: ScopeChecker): boolean =>
  hasLaborApproveAccess(has) || has('EDIT_LABOR_VALIDATION');

/**
 * May this user open the table at all? "View Labor Validation" grants a read-only view;
 * edit rights imply it, so a user holding only the edit scope is not locked out.
 */
export const hasLaborValidationViewAccess = (has: ScopeChecker): boolean =>
  has('VIEW_LABOR_VALIDATION') || hasLaborValidationEditAccess(has);

/**
 * Labor-code dropdown gating (Segment B migration — was `permissionData.includes('Apply * Labor
 * Code')` off `src/paths.ts` in `edit-labor-management.tsx` / `time-in-time-out.tsx`).
 *
 * Each building labor code (`code.key`) is shown only when the user holds the matching
 * `APPLY_*_LABOR_CODE` scopeKey. The catalog display names are identical to the legacy
 * paths.ts strings (parity-verified), so behavior is preserved — only the check mechanism
 * moves from human-name `includes()` to scopeKey `has()`, which is what emits RUM telemetry.
 *
 * `REHAB_TRAV` has a gate (`APPLY_REHAB_TRAV_LABOR_CODE`) but no role grants it in the
 * catalog — a permanent-deny by design; the mapping stays so the code path is honest.
 */
export const LABOR_CODE_SCOPE: Record<string, string> = {
  REHAB_TRAV: Permissions.APPLY_REHAB_TRAV_LABOR_CODE,
  FLOAT: Permissions.APPLY_FLOAT_LABOR_CODE,
  PRNEDU: Permissions.APPLY_PRNEDU_LABOR_CODE,
  BEREV: Permissions.APPLY_BEREV_LABOR_CODE,
  HOL: Permissions.APPLY_HOL_LABOR_CODE,
  PTO: Permissions.APPLY_PTO_LABOR_CODE,
  JURY: Permissions.APPLY_JURY_LABOR_CODE,
};

/** Is a single building labor code visible to this user? */
export const canApplyLaborCode = (has: ScopeChecker, codeKey: string): boolean => {
  const scope = LABOR_CODE_SCOPE[codeKey];
  return scope ? has(scope) : false;
};

/** Filter the labor-code options down to the ones this user may apply. */
export const filterLaborCodesByScope = <T extends { key?: string }>(
  codes: T[] | undefined,
  has: ScopeChecker
): T[] => (codes ?? []).filter((code) => canApplyLaborCode(has, code?.key ?? ''));

/**
 * Show the labor-code dropdown at all? True when the user may apply at least one code.
 * Mirrors the old `isLaborCodeDropdownShow` OR-chain over the seven `Apply * Labor Code`
 * permissions.
 */
export const isLaborCodeDropdownVisible = (has: ScopeChecker): boolean =>
  Object.values(LABOR_CODE_SCOPE).some((scope) => has(scope));
